import './style.scss';

import {
  autoPlacement,
  autoUpdate,
  offset,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import cn from 'classnames';
import { motion } from 'framer-motion';
import { useCallback, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { v4 as uuid } from 'uuid';

import { useThemeColors } from '@/shared/theme';
import { SmartFloatingPortal as FloatingPortal } from '@/shared/ui/SmartFloatingPortal';

import { useTypedSelector } from 'store';
import { useFilters } from 'store/slices/filters/hooks/useFilters';
import { Bus } from 'tools';
import {
  EArbitrageFilters,
  ECexPairFilters,
  EDexPairFilters,
  EGeneralFilters,
  EProjectFilters,
  ESolPairFilters,
} from 'types/filters';
import { IFilterScheme } from 'types/filters/common';
import { ICexPair, IDexPair } from 'types/pairs';
import { EExchange } from 'web3';

import FiltersList from '../FiltersList';
import { SettedFilters } from '../SettedFilters';

import { FilterIcon } from 'assets/icons';

interface IFilterPopupProps<V> {
  dexPair?: IDexPair;
  solPair?: IDexPair;
  cexPair?: ICexPair;
  exchange?: V;
  general?: EGeneralFilters;
  arbitrage?: EArbitrageFilters;
  type?: V extends EExchange.cex
    ? ECexPairFilters
    : V extends EExchange.sol
      ? ESolPairFilters
      : EDexPairFilters;
  projectId?: number;
  projectType?: EProjectFilters;
}

function FiltersPopup<V extends EExchange>({
  dexPair: dexPairProp,
  solPair: solPairProp,
  cexPair: cexPairProp,
  exchange,
  arbitrage,
  general,
  type,
  projectId,
  projectType,
}: IFilterPopupProps<V>) {
  const dexPair = dexPairProp ?? useTypedSelector(store => store.pairs.selectedDexPair);
  const solPair = solPairProp ?? useTypedSelector(store => store.pairs.selectedSolPair);
  const cexPair = cexPairProp ?? useTypedSelector(store => store.pairs.selectedCexPair);
  const palette = useThemeColors();

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const id = useMemo(() => uuid(), []);

  const { scheme, pairFilters, isFilterSetted, clearFilters, saveFilters, settedPairFilters } =
    useFilters({
      general,
      arbitrage,
      project: projectId && projectType ? { projectId, type: projectType } : undefined,
      cex:
        exchange === EExchange.cex && type
          ? { pair: cexPair ?? undefined, type: type as ECexPairFilters }
          : undefined,
      dex:
        exchange === EExchange.dex && type
          ? { pair: dexPair ?? undefined, type: type as EDexPairFilters }
          : undefined,
      sol:
        exchange === EExchange.sol && type
          ? { pair: solPair ?? undefined, type: type as ESolPairFilters }
          : undefined,
    });

  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);

  const { refs, context, floatingStyles } = useFloating({
    placement: 'bottom-start',
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      autoPlacement({ allowedPlacements: ['bottom-start', 'top-start'], padding: 5 }),
      offset(10),
      shift({ padding: 5 }),
      size({
        apply({ availableHeight }) {
          flushSync(() => {
            setMaxHeight(availableHeight);
          });
        },
      }),
    ],
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

  const handleAddFilter = useCallback(() => {
    Bus.emit(`${id}-FILTERS-ADD`);
  }, [id]);

  const handleFilter = useCallback(() => {
    setIsOpen(false);
    saveFilters();

    setTimeout(() => {
      if (general) {
        Bus.emit(`${general}:FILTER`);
      } else if (arbitrage) {
        Bus.emit(`arbitrage:${arbitrage}:FILTER`);
      } else if (projectId && projectType) {
        Bus.emit(`project:${projectId}:${projectType}:FILTER`);
      } else {
        Bus.emit(`${exchange}:${type}:FILTER`);
      }
    }, 100);
  }, [saveFilters, exchange, type, general, arbitrage, projectId, projectType]);

  const handleClearFilters = useCallback(() => {
    setIsOpen(false);
    clearFilters();

    setTimeout(() => {
      saveFilters();
      setTimeout(() => {
        if (general) {
          Bus.emit(`${general}:FILTER`);
        } else if (arbitrage) {
          Bus.emit(`arbitrage:${arbitrage}:FILTER`);
        } else if (projectId && projectType) {
          Bus.emit(`project:${projectId}:${projectType}:FILTER`);
        } else {
          Bus.emit(`${exchange}:${type}:FILTER`);
        }
      }, 100);
    }, 0);
  }, [clearFilters, saveFilters, exchange, type, general, arbitrage, projectId, projectType]);

  const isAddFilterDisabled = useMemo(() => {
    if (!scheme) return true;

    const singleFiltersCount = Object.values(scheme).filter(
      filter => (filter as IFilterScheme).single,
    ).length;

    const hasNoMultiple = singleFiltersCount === Object.values(scheme).length;

    return hasNoMultiple && pairFilters.length === singleFiltersCount;
  }, [pairFilters, scheme]);

  return (
    <>
      <div>
        <motion.div
          whileHover={{ filter: 'brightness(80%)' }}
          className={cn('mm-filter-popup-trigger-button', { is_setted: isFilterSetted })}
          ref={refs.setReference}
          {...getReferenceProps()}
        >
          <FilterIcon />
          <span>Filters</span>
        </motion.div>
        <SettedFilters isFilterSetted={isFilterSetted} settedFilters={settedPairFilters} />
      </div>
      {isOpen && (
        <FloatingPortal>
          <div
            className="mm-filters-popup-container"
            ref={refs.setFloating}
            style={{ ...floatingStyles, maxHeight }}
            {...getFloatingProps()}
          >
            <div className="filters-header">
              <span className="filters-title">Filters</span>
              <div className="filter-header-right">
                <motion.button
                  whileHover={{ backgroundColor: palette.light_gray_2 }}
                  className="clear-filters"
                  onClick={handleClearFilters}
                >
                  Clear all
                </motion.button>
              </div>
            </div>
            <FiltersList
              dexPair={dexPair ?? undefined}
              cexPair={cexPair ?? undefined}
              solPair={solPair ?? undefined}
              id={id}
              exchange={exchange}
              general={general}
              arbitrage={arbitrage}
              type={type}
              projectId={projectId}
              projectType={projectType}
            />
            <div className="filters-footer">
              <motion.button
                whileHover={
                  !isAddFilterDisabled ? { backgroundColor: palette.light_gray_2 } : undefined
                }
                className={cn('filters-add-filter', { disabled: isAddFilterDisabled })}
                onClick={handleAddFilter}
                disabled={isAddFilterDisabled}
              >
                <span>+ Add filter</span>
              </motion.button>
              <motion.button
                whileHover={{ backgroundColor: palette.primary_dark }}
                className="filters-start-filter"
                onClick={handleFilter}
              >
                <span>Filter</span>
              </motion.button>
            </div>
          </div>
        </FloatingPortal>
      )}
    </>
  );
}

export default FiltersPopup;
